import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from 'helpers/api';
import cookie from 'react-cookies';

import { disable2FA, enable2FA } from 'api/user-slice';

const initialState = {
  company_id: null,
  user_id: null,
  admin_permissions: [],
  checked: false,
  logged: false,
  admin: false,
  two_factor_auth_enabled: false,
  enforce_two_factor_auth: false,
};

export const authenticate = createAsyncThunk('authenticate', async () => {
  const res = await apiRequest('authentication', {}, 'get', true);
  return res.result;
});

export const verify2fa = createAsyncThunk('verify2fa', async ({ user_id, code }) => {
  const res = await apiRequest('authentication/2fa', { user_id, code }, 'post', true);
  return res.result;
});

export const login = createAsyncThunk('login', async ({ email, password }) => {
  const res = await apiRequest('authentication', { email, password }, 'post', true);
  return res;
});

export const loginWithGoogle = createAsyncThunk(
  'loginWithGoogle',
  async ({ credential, client_id }) => {
    const res = await apiRequest('authentication', { credential, client_id }, 'post', true);
    return res;
  },
);

export const loginAs = createAsyncThunk('loginAs', async userId => {
  const res = await apiRequest(`authentication/login_as/${userId}`, {}, 'post');
  return res.result;
});

export const verifySSO = createAsyncThunk('verifySSO', async code => {
  const res = await apiRequest('authentication/sso', { code }, 'post', true);
  return res.result;
});

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    logout(state) {
      cookie.remove('token', { path: '/' });
      state.checked = true;
      state.logged = false;
      state.admin = false;
      state.two_factor_auth_enabled = false;
      state.enforce_two_factor_auth = false;
      state.admin_permissions = [];
    },
    stopInterpretation(state) {
      cookie.save('token', cookie.load('admin-token'), { path: '/' });
      cookie.remove('admin-token', { path: '/' });
      state.checked = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authenticate.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(authenticate.rejected, state => {
        state.checked = true;
      })
      .addCase(verify2fa.fulfilled, (state, { payload }) => {
        if (!payload?.verified) return;

        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        if (payload.error || payload.result?.two_factor_auth_enabled) return;

        state.company_id = payload.result.company_id;
        state.user_id = payload.result.user_id;
        state.admin = payload.result.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(loginAs.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(loginWithGoogle.fulfilled, (state, { payload }) => {
        if (payload.error) return;

        state.company_id = payload.result.company_id;
        state.user_id = payload.result.user_id;
        state.admin = payload.result.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(verifySSO.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
        state.admin_permissions = payload.admin_permissions;
      })
      .addCase(enable2FA.fulfilled, state => {
        state.two_factor_auth_enabled = true;
      })
      .addCase(disable2FA.fulfilled, state => {
        state.two_factor_auth_enabled = false;
      });
  },
});

export const { logout, stopInterpretation } = authenticationSlice.actions;

export const selectAuthentication = state => state.authentication;

export default authenticationSlice.reducer;
